/*
 * WHAT THE DASHBOARD IS TOLD WHILE IT SHOWS A SHARED SNIPPET. The dashboard asks its server for the day's visits, the site's pages and the day's numbers. In the viewer it must get the snippet's, and ONLY the snippet's:
 * the frozen visits, the pages as the owner had the boxes, and nothing of the rest of the day (the day's hourly numbers and totals would say how busy the site was beyond what was shared). Everything else the
 * dashboard asks for is answered as before, so the screen builds exactly as it does for the owner. `shareAnswer` returns the replacement, or undefined to leave the answer alone.
 */
import { ShareSnapshot } from "./shareSnapshot";

let active: ShareSnapshot | null = null;
export const setActiveShare = (snapshot: ShareSnapshot | null) => { active = snapshot; };
export const getActiveShare = (): ShareSnapshot | null => active;

/**
 * A replay clip's page boxes belong to the recipient while they watch: they can move, swap and remove them, and Reset brings them back. The server that answers the dashboard (the demo's, here) holds them: the viewer
 * hands it the clip's boxes once, and from then on the dashboard's own page changes edit that copy and its answers carry them. Without a server that took the boxes, the clip's frozen ones are what is answered.
 */
let seeder: ((pages: Array<{ _id: string; name: string; selected: boolean; slotIndex: number | null; restricted: boolean }>) => void) | null = null;
let seeded = false;
export const registerPageSeeder = (fn: typeof seeder) => { seeder = fn; };
export const seedSharePages = (snapshot: ShareSnapshot | null) => {
  seeded = false;
  if (!snapshot || !seeder) return;
  seeder(pagesOf(snapshot));
  seeded = true;
};
const pagesOf = (snapshot: ShareSnapshot) => snapshot.pages.map((p, i) => ({ _id: `p${i}`, name: p.name, selected: p.selected, slotIndex: p.slotIndex, restricted: false }));

export const shareAnswer = (method: string, base: any, snapshot: ShareSnapshot | null = active): any => {
  if (!snapshot) return undefined;
  switch (method) {
    case "getSite":
    case "getUsersSite":
      return { ...(base || {}), name: snapshot.siteName, pages: seeded && Array.isArray(base?.pages) ? base.pages : pagesOf(snapshot) };
    case "getUsersSites": {
      const one = (Array.isArray(base) ? base[0] : base) || {};

      return [{ ...one, name: snapshot.siteName, pages: seeded && Array.isArray(one.pages) ? one.pages : pagesOf(snapshot) }];
    }
    case "getPlaybackTimeline":
      // a visit's createdAt is kept as ms from the clip's start; the replay's clock is the real time of day, so the tooltip's "session time" needs it absolute (a visit already there at the start counts from the start)
      return { date: "", actions: snapshot.actions.map((a) => (a.type === "arrival" ? { ...a, session: { ...a.session, createdAt: snapshot.startMs + (Number(a.session?.createdAt) || 0), ...(a.t === 0 ? { carried: true } : {}) } } : a)), allSessions: [], sessionCount: snapshot.actions.filter((a) => a.type === "arrival").length, sampled: snapshot.sampled, truncated: false, restricted: false };
    case "getPlaybackDayMetrics": {
      // the dashboard shows "No sessions this day" when this says zero: it is the CLIP's own visit count (nothing about the rest of the day)
      const visits = snapshot.actions.filter((a) => a.type === "arrival").length;

      return { date: "", hourly: [], summary: { totalSession: visits, bounceRate: "0", avgSessionDuration: 0, newUsers: 0, pageviewsPerSession: 0 } };
    }
    case "getPlaybackConvertedUrls":
      return snapshot.goalUrls;
    case "getPlaybackDaysWithData":
      return [];
    case "getPlaybackDayCounts":
      return {};
    case "getNotes":
      return { ok: true, notes: [] };
    // nothing LIVE is told in a snippet's viewer: no one is "on the site now" (the dashboard's resync would otherwise draw the demo's own visitors onto the replay's stage), and the live range's dock is empty
    case "getOnsiteSessions":
      return [];
    case "getHistoricalBubblesData":
      return {};
    default:
      return undefined;
  }
};
